// Dashboard_Requisition.js - Elegant Requisition Dashboard
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  Avatar,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Badge,
  Divider,
} from '@mui/material';

import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

import {
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  LocalShipping as LocalShippingIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  DoneAll as DoneAllIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Receipt as ReceiptIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Warning as WarningIcon,
  Verified as VerifiedIcon,
  ThumbUp as ThumbUpIcon,
  Print as PrintIcon,
} from '@mui/icons-material';

import { styled, keyframes } from '@mui/material/styles';
import Sidebar from '../../../../components/sidebars/Sidebar_Requisition';
import TopNav from '../../../../components/TopNav_hotelStaff';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 24,
  transition: 'all 0.3s ease-in-out',
  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
  border: '1px solid rgba(103, 58, 183, 0.1)',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(103, 58, 183, 0.15)',
  },
}));

const StatCard = styled(Paper)(({ theme, color }) => ({
  padding: theme.spacing(3),
  borderRadius: 28,
  background: `linear-gradient(135deg, ${color || '#667eea'} 0%, ${color || '#764ba2'} 100%)`,
  color: 'white',
  position: 'relative',
  overflow: 'hidden',
  animation: `${fadeInUp} 0.6s ease-out`,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -30,
    right: -30,
    width: 150,
    height: 150,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.1)',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -30,
    left: -30,
    width: 100,
    height: 100,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.05)',
  },
}));

const GlowingIcon = styled(Avatar)(({ theme }) => ({
  background: 'rgba(255,255,255,0.2)',
  width: 64,
  height: 64,
  boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
  '& svg': {
    fontSize: 36,
  },
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
  borderRadius: '20px',
  fontWeight: 600,
  fontSize: '0.75rem',
  height: '28px',
  ...(status === 'Received' && {
    backgroundColor: '#dcfce7',
    color: '#166534',
    border: '1px solid #86efac',
  }),
  ...(status === 'Issued' && {
    backgroundColor: '#dbeafe',
    color: '#1e40af',
    border: '1px solid #93c5fd',
  }),
  ...(status === 'Approved by GM' && {
    backgroundColor: '#dbeafe',
    color: '#1e40af',
    border: '1px solid #93c5fd',
  }),
  ...(status === 'Approved by Dept Head' && {
    backgroundColor: '#fef3c7',
    color: '#92400e',
    border: '1px solid #fde68a',
  }),
  ...(status === 'Pending' && {
    backgroundColor: '#fef3c7',
    color: '#92400e',
    border: '1px solid #fde68a',
  }),
  ...(status === 'Rejected' && {
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    border: '1px solid #fca5a5',
  }),
}));

const COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316', '#10b981'];

const DashboardRequisition = () => {
  const isMobile = useMediaQuery('(max-width:768px)');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedRequisition, setSelectedRequisition] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  
  // User data
  const [loggedInUser, setLoggedInUser] = useState('');
  const [userDepartment, setUserDepartment] = useState('');
  
  // Data states
  const [requisitions, setRequisitions] = useState([]);
  const [totalRequisitions, setTotalRequisitions] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    issued: 0,
    completed: 0,
    rejected: 0,
    monthly_trend: [],
    department_stats: []
  });

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const showNotification = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Get user details from localStorage
  const getUserDetails = useCallback(() => {
    try {
      const userProfileStr = localStorage.getItem('userProfile');
      if (userProfileStr) {
        const userProfile = JSON.parse(userProfileStr);
        return {
          full_name: userProfile.full_name || `${userProfile.first_name || ''} ${userProfile.other_names || ''}`.trim() || userProfile.email || 'Staff',
          department: userProfile.department || '',
          role: userProfile.user_type || 'staff'
        };
      }
      return {
        full_name: localStorage.getItem('userName') || localStorage.getItem('email') || 'Staff',
        department: localStorage.getItem('userDepartment') || localStorage.getItem('department') || '',
        role: localStorage.getItem('role') || 'staff'
      };
    } catch (error) {
      console.error('Error getting user details:', error);
      return { full_name: 'Staff', department: '', role: 'staff' };
    }
  }, []);

  // Fetch requisitions data
  const fetchRequisitions = useCallback(async () => {
    try {
      setLoading(true);
      const user = getUserDetails();
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (user.department) params.append('department', user.department);
      params.append('limit', 100);
      params.append('offset', 0);

      const response = await axios.get(`${API_BASE_URL}/hotel/inventory/get_requisitions.php?${params}`);
      
      if (response.data.success) {
        const requisitionData = response.data.data;
        setRequisitions(requisitionData);
        setTotalRequisitions(response.data.total);
        
        // Calculate statistics
        const pending = requisitionData.filter(r => 
          !r.approved_by_dept_head && !r.approved_by_GM && r.status !== 'Rejected'
        ).length;
        const approved = requisitionData.filter(r => 
          r.approved_by_GM && !r.issued_by && r.status !== 'Rejected'
        ).length;
        const issued = requisitionData.filter(r => 
          r.issued_by && !r.received_by && r.status !== 'Rejected'
        ).length;
        const completed = requisitionData.filter(r => r.received_by).length;
        const rejected = requisitionData.filter(r => r.status === 'Rejected').length;
        
        // Monthly trend data
        const monthlyMap = new Map();
        requisitionData.forEach(req => {
          const month = new Date(req.request_date).toLocaleString('default', { month: 'short' });
          if (!monthlyMap.has(month)) {
            monthlyMap.set(month, { month, count: 0, approved: 0 });
          }
          const data = monthlyMap.get(month);
          data.count++;
          if (req.approved_by_GM) data.approved++;
        });
        
        const monthly_trend = Array.from(monthlyMap.values()).slice(-6);
        
        // Department stats
        const deptMap = new Map();
        requisitionData.forEach(req => {
          if (!deptMap.has(req.department)) {
            deptMap.set(req.department, { department: req.department, count: 0 });
          }
          deptMap.get(req.department).count++;
        });
        const department_stats = Array.from(deptMap.values()).slice(0, 5);
        
        setStats({
          total: response.data.total,
          pending,
          approved,
          issued,
          completed,
          rejected,
          monthly_trend,
          department_stats
        });
      } else {
        showNotification(response.data.message || 'Failed to load requisitions', 'error');
      }
    } catch (error) {
      console.error('Error fetching requisitions:', error);
      showNotification('Failed to connect to server', 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [searchTerm, getUserDetails]);

  const refreshData = async () => {
    setRefreshing(true);
    await fetchRequisitions();
    showNotification('Dashboard refreshed successfully', 'success');
  };

  useEffect(() => {
    const user = getUserDetails();
    setLoggedInUser(user.full_name);
    setUserDepartment(user.department);
    fetchRequisitions();
  }, [fetchRequisitions, getUserDetails]);

  const filteredRequisitions = requisitions.filter(req =>
    req.item_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    req.requested_by?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch(status) {
      case 'Received': return '#10b981';
      case 'Issued': return '#3b82f6';
      case 'Approved by GM': return '#3b82f6';
      case 'Approved by Dept Head': return '#f59e0b';
      case 'Pending': return '#f59e0b';
      case 'Rejected': return '#ef4444';
      default: return '#94a3b8';
    }
  };

  const getStatusIcon = (requisition) => {
    if (requisition.received_by) return <DoneAllIcon sx={{ fontSize: 16 }} />;
    if (requisition.issued_by) return <LocalShippingIcon sx={{ fontSize: 16 }} />;
    if (requisition.approved_by_GM) return <VerifiedIcon sx={{ fontSize: 16 }} />;
    if (requisition.approved_by_dept_head) return <ThumbUpIcon sx={{ fontSize: 16 }} />;
    return <HourglassEmptyIcon sx={{ fontSize: 16 }} />;
  };

  const handleViewDetails = (requisition) => {
    setSelectedRequisition(requisition);
    setViewDialogOpen(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <CircularProgress size={80} thickness={4} sx={{ color: 'white' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#f0f4fc' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { xs: '100%', md: `calc(100% - ${sidebarOpen ? 260 : 0}px)` },
          minHeight: '100vh',
          overflow: 'auto',
          transition: 'width 0.3s ease',
          background: 'linear-gradient(135deg, #f5f7ff 0%, #eef2ff 100%)',
        }}
      >
        <TopNav
          onLogout={() => {
            localStorage.removeItem('authToken');
            localStorage.removeItem('userProfile');
            localStorage.removeItem('phoneNumber');
          }}
          onNotificationClick={() => {
            showNotification(`You have ${stats.pending} pending requisitions`, 'info');
          }}
          toggleSidebar={toggleSidebar}
        />

        {/* Header Section */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 5, mt: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight="800" sx={{ color: '#1e1b4b', letterSpacing: '-0.02em' }}>
              Requisition Dashboard
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
              Track and manage all your department requisitions
            </Typography>
            {userDepartment && (
              <Chip 
                icon={<BusinessIcon />}
                label={`Department: ${userDepartment}`}
                size="small"
                sx={{ mt: 1, bgcolor: '#eef2ff', color: '#6366f1' }}
              />
            )}
          </Box>
          <Tooltip title="Refresh Data">
            <IconButton 
              onClick={refreshData} 
              disabled={refreshing} 
              sx={{ 
                bgcolor: 'white', 
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                '&:hover': { bgcolor: '#f8f9ff', transform: 'rotate(180deg)', transition: 'transform 0.5s' }
              }}
            >
              <RefreshIcon sx={{ color: '#6366f1' }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 5 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard elevation={0} color="#6366f1">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 500 }}>Total</Typography>
                  <Typography variant="h2" fontWeight="800" sx={{ fontSize: '2rem', mt: 1 }}>{stats.total}</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', mt: 1 }}>
                    All Requisitions
                  </Typography>
                </Box>
                <GlowingIcon>
                  <AssignmentIcon sx={{ fontSize: 32 }} />
                </GlowingIcon>
              </Box>
            </StatCard>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard elevation={0} color="#f59e0b">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 500 }}>Pending</Typography>
                  <Typography variant="h2" fontWeight="800" sx={{ fontSize: '2rem', mt: 1 }}>{stats.pending}</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', mt: 1 }}>
                    Awaiting Approval
                  </Typography>
                </Box>
                <GlowingIcon>
                  <HourglassEmptyIcon sx={{ fontSize: 32 }} />
                </GlowingIcon>
              </Box>
            </StatCard>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard elevation={0} color="#3b82f6">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 500 }}>Approved</Typography>
                  <Typography variant="h2" fontWeight="800" sx={{ fontSize: '2rem', mt: 1 }}>{stats.approved}</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', mt: 1 }}>
                    Ready for Issuance
                  </Typography>
                </Box>
                <GlowingIcon>
                  <VerifiedIcon sx={{ fontSize: 32 }} />
                </GlowingIcon>
              </Box>
            </StatCard>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard elevation={0} color="#10b981">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 500 }}>Completed</Typography>
                  <Typography variant="h2" fontWeight="800" sx={{ fontSize: '2rem', mt: 1 }}>{stats.completed}</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', mt: 1 }}>
                    Received & Closed
                  </Typography>
                </Box>
                <GlowingIcon>
                  <DoneAllIcon sx={{ fontSize: 32 }} />
                </GlowingIcon>
              </Box>
            </StatCard>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard elevation={0} color="#ef4444">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 500 }}>Rejected</Typography>
                  <Typography variant="h2" fontWeight="800" sx={{ fontSize: '2rem', mt: 1 }}>{stats.rejected}</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', mt: 1 }}>
                    Not Approved
                  </Typography>
                </Box>
                <GlowingIcon>
                  <CancelIcon sx={{ fontSize: 32 }} />
                </GlowingIcon>
              </Box>
            </StatCard>
          </Grid>
        </Grid>

        {/* Charts Section */}
        <Grid container spacing={3} sx={{ mb: 5 }}>
          <Grid item xs={12} lg={7}>
            <StyledCard>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" fontWeight="700" sx={{ color: '#1e1b4b' }}>
                    Requisition Trends
                  </Typography>
                  <Chip label="Last 6 Months" size="small" sx={{ bgcolor: '#eef2ff', color: '#6366f1', fontWeight: 500 }} />
                </Box>
                <ResponsiveContainer width="100%" height={320}>
                  <AreaChart data={stats.monthly_trend}>
                    <defs>
                      <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" stroke="#94a3b8" />
                    <YAxis stroke="#94a3b8" />
                    <RechartsTooltip />
                    <Area type="monotone" dataKey="count" stroke="#6366f1" fill="url(#colorCount)" name="Requisitions" strokeWidth={3} />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </StyledCard>
          </Grid>
          
          <Grid item xs={12} lg={5}>
            <StyledCard>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="700" sx={{ color: '#1e1b4b', mb: 3 }}>
                  Department Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart>
                    <Pie
                      data={stats.department_stats}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={3}
                      dataKey="count"
                      nameKey="department"
                    >
                      {stats.department_stats.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="none" />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                    <Legend verticalAlign="bottom" height={36} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </StyledCard>
          </Grid>
        </Grid>

        {/* Recent Requisitions Table */}
        <StyledCard>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h6" fontWeight="700" sx={{ color: '#1e1b4b', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ReceiptIcon sx={{ color: '#6366f1' }} />
                  Recent Requisitions
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                  Your latest department requisitions
                </Typography>
              </Box>
              <TextField
                size="small"
                placeholder="Search by item or requester..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{ 
                  startAdornment: <SearchIcon sx={{ mr: 1, color: '#94a3b8' }} />,
                  sx: { borderRadius: 3, bgcolor: 'white', minWidth: 250 }
                }}
              />
            </Box>
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Item Name</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: '#475569' }}>Quantity</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Unit</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Requested By</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Status</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: '#475569' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRequisitions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                        <CheckCircleIcon sx={{ fontSize: 48, color: '#10b981', mb: 2 }} />
                        <Typography color="text.secondary">No requisitions found</Typography>
                        <Typography variant="caption" color="text.secondary">All requisitions are up to date</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRequisitions.slice(0, 10).map((req) => (
                      <TableRow key={req.id} hover sx={{ '&:hover': { bgcolor: '#fefce8' } }}>
                        <TableCell>
                          <Typography fontWeight="600" sx={{ color: '#1e1b4b' }}>#{req.id}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight="500">{req.item_name}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="600">{req.quantity_requested}</Typography>
                        </TableCell>
                        <TableCell>{req.unit_of_measurement || 'N/A'}</TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <PersonIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                            <Typography variant="body2">{req.requested_by}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <CalendarIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                            <Typography variant="body2">{req.request_date}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <StatusChip 
                            icon={getStatusIcon(req)}
                            label={req.status || 'Pending'} 
                            status={req.status || 'Pending'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Details">
                            <IconButton 
                              size="small" 
                              onClick={() => handleViewDetails(req)}
                              sx={{ color: '#6366f1' }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </StyledCard>
      </Box>

      {/* View Requisition Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={700} sx={{ color: '#1e1b4b' }}>
              Requisition Details #{selectedRequisition?.id}
            </Typography>
            <StatusChip 
              label={selectedRequisition?.status || 'Pending'} 
              status={selectedRequisition?.status || 'Pending'}
              size="small"
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRequisition && (
            <>
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#6366f1', mb: 2 }}>
                Request Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Department</Typography>
                  <Typography variant="body1" fontWeight={500}>{selectedRequisition.department}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Item Name</Typography>
                  <Typography variant="body1" fontWeight={500}>{selectedRequisition.item_name}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Quantity Requested</Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#1e1b4b' }}>
                    {selectedRequisition.quantity_requested} {selectedRequisition.unit_of_measurement || ''}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Requested By</Typography>
                  <Typography variant="body1">{selectedRequisition.requested_by}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Request Date</Typography>
                  <Typography variant="body1">{selectedRequisition.request_date}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Last Updated</Typography>
                  <Typography variant="body1">{selectedRequisition.updated_at || selectedRequisition.request_date}</Typography>
                </Grid>
                {selectedRequisition.notes && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Notes / Purpose</Typography>
                    <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: '#f8fafc' }}>
                      <Typography variant="body2">{selectedRequisition.notes}</Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#6366f1', mb: 2 }}>
                Approval Workflow
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: selectedRequisition.approved_by_dept_head ? '#f0fdf4' : '#fefce8' }}>
                    <ThumbUpIcon sx={{ fontSize: 32, color: selectedRequisition.approved_by_dept_head ? '#10b981' : '#f59e0b', mb: 1 }} />
                    <Typography variant="caption" color="text.secondary" display="block">Dept Head Approval</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {selectedRequisition.approved_by_dept_head || 'Pending'}
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: selectedRequisition.approved_by_GM ? '#f0fdf4' : '#fefce8' }}>
                    <VerifiedIcon sx={{ fontSize: 32, color: selectedRequisition.approved_by_GM ? '#10b981' : '#f59e0b', mb: 1 }} />
                    <Typography variant="caption" color="text.secondary" display="block">GM Approval</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {selectedRequisition.approved_by_GM || 'Pending'}
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: selectedRequisition.issued_by ? '#eff6ff' : '#fefce8' }}>
                    <LocalShippingIcon sx={{ fontSize: 32, color: selectedRequisition.issued_by ? '#3b82f6' : '#f59e0b', mb: 1 }} />
                    <Typography variant="caption" color="text.secondary" display="block">Issued By</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {selectedRequisition.issued_by || 'Pending'}
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: selectedRequisition.received_by ? '#f0fdf4' : '#fefce8' }}>
                    <DoneAllIcon sx={{ fontSize: 32, color: selectedRequisition.received_by ? '#10b981' : '#f59e0b', mb: 1 }} />
                    <Typography variant="caption" color="text.secondary" display="block">Received By</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {selectedRequisition.received_by || 'Pending'}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)} variant="outlined">Close</Button>
          <Button variant="contained" startIcon={<PrintIcon />} sx={{ bgcolor: '#6366f1' }}>
            Print
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ borderRadius: 3 }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default DashboardRequisition;